import {
  collection,
  doc,
  getDoc,
  getDocs,
  limit,
  orderBy,
  query,
  startAfter,
  Timestamp,
  where,
  type QueryDocumentSnapshot,
} from 'firebase/firestore';

import { FSCollections } from '@src/constants/database';
import { db } from '@src/firebase/firebase';
import { getDocData } from '@src/packages/utils';
import safeExecute from '@src/util/safeExecute';

const ATTENDANCE_PAGE_SIZE = 500;

interface GetAttendanceRecordParams {
  organizationId: string;
  attendanceRecordId: string;
}

interface ListByEmployeeInScheduledPeriodParams {
  organizationId: string;
  employeeId: string;
  startMillis: number;
  endMillis: number;
}

class AttendanceRecordRepository {
  getAttendanceRecordPath(organizationId: string, attendanceRecordId: string): string {
    return `${FSCollections.ORGANIZATIONS}/${organizationId}/${FSCollections.ATTENDANCE_RECORDS}/${attendanceRecordId}`;
  }

  async getAttendanceRecord({
    organizationId,
    attendanceRecordId,
  }: GetAttendanceRecordParams): Promise<unknown> {
    return safeExecute(async () => {
      const attendanceRecordRef = doc(
        db,
        this.getAttendanceRecordPath(organizationId, attendanceRecordId),
      );
      const attendanceRecordSnap = await getDoc(attendanceRecordRef);

      return getDocData(attendanceRecordSnap);
    });
  }

  async listByEmployeeInScheduledPeriod({
    organizationId,
    employeeId,
    startMillis,
    endMillis,
  }: ListByEmployeeInScheduledPeriodParams): Promise<unknown[]> {
    return safeExecute(async () => {
      if (!organizationId || !employeeId) {
        return [];
      }

      const rangeStart = Timestamp.fromMillis(startMillis);
      const rangeEnd = Timestamp.fromMillis(endMillis);

      const colRef = collection(
        db,
        FSCollections.ORGANIZATIONS,
        organizationId,
        FSCollections.ATTENDANCE_RECORDS,
      );

      const baseConstraints = [
        where('employeeId', '==', employeeId),
        where('scheduledStartAt', '>=', rangeStart),
        where('scheduledStartAt', '<=', rangeEnd),
        orderBy('scheduledStartAt', 'asc'),
      ] as const;

      const rows: unknown[] = [];
      let lastDoc: QueryDocumentSnapshot | null = null;

      for (;;) {
        const pageQuery = lastDoc
          ? query(colRef, ...baseConstraints, startAfter(lastDoc), limit(ATTENDANCE_PAGE_SIZE))
          : query(colRef, ...baseConstraints, limit(ATTENDANCE_PAGE_SIZE));

        const snap = await getDocs(pageQuery);
        if (snap.empty) {
          break;
        }

        snap.docs.forEach(docSnap => {
          rows.push(docSnap.data());
        });

        lastDoc = snap.docs[snap.docs.length - 1];
        if (snap.size < ATTENDANCE_PAGE_SIZE) {
          break;
        }
      }

      return rows;
    });
  }
}

export default AttendanceRecordRepository;
